// Typed REST client over the Cloud Run REST facade (or `roast serve` locally).
//
// Every call:
//   * attaches a fresh Firebase ID token (or a placeholder in
//     VITE_AUTH_DISABLED mode — the local server ignores it)
//   * routes through a single `request` helper so error shapes are uniform
//   * returns parsed JSON typed against ./types.ts
//
// We don't do any retries. The backend is idempotent for GETs, and every
// write surface (rate, correct, add_coffee_profile) is safe to re-submit —
// but we let the caller decide, the hook surfaces the error and the user
// hits the button again.
//
// Envelope shape contract. Every handler in `roastmcp/handlers.py` returns
// `{text: str, data: {...}}` — `text` is a short template-rendered line we
// surface verbatim, `data` is the structured payload. The methods below
// unwrap that envelope before handing results back to components so pages
// don't have to know the envelope exists. The analysis endpoints
// (improvement suggestions, reference roast, consistency report) still
// return the full envelope because the caller displays the `text` line
// alongside the structured data. Pure wire→page adapters live in
// ./envelope.ts.

import type {
  AIEvaluation,
  CoffeeProfile,
  ConsistencyReportResponseData,
  CorrectionInput,
  DetectedMilestonesData,
  Envelope,
  Evaluation,
  ImprovementSuggestionsData,
  IngestionStatus,
  ListRoastsResponse,
  MaintenanceEvent,
  MaintenanceStatusData,
  MaintenanceStatusResponse,
  MarkerDiscrepanciesData,
  MilestoneConfidenceData,
  RatingInput,
  Rating,
  RecipeTrend,
  RecipeTrendData,
  ReferenceRoastResponseData,
  RoastDetail,
  RoastPageData,
  Timeseries,
} from "./types";
import { env } from "./env";
import {
  adaptIngestionStatus,
  envelopeToRoastList,
  transposeTimeseries,
  type IngestionStatusData,
  type TimeseriesData,
} from "./envelope";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = "ApiError";
  }
}

type FetchLike = typeof fetch;

export interface ApiOptions {
  getIdToken: () => Promise<string>;
  baseUrl?: string;
  fetchImpl?: FetchLike; // injected for tests
}

export interface ListRoastsParams {
  coffee_id?: string;
  min_stars?: number;
  since?: string;
  until?: string;
  limit?: number;
  cursor?: string | null;
}

export class Api {
  private readonly getIdToken: () => Promise<string>;
  private readonly baseUrl: string;
  private readonly fetchImpl: FetchLike;

  constructor(opts: ApiOptions) {
    this.getIdToken = opts.getIdToken;
    this.baseUrl = (opts.baseUrl ?? env.apiBaseUrl).replace(/\/$/, "");
    // `.bind(window)` is required: Safari throws "Can only call
    // Window.fetch on instances of Window" if `fetch` is stashed on a
    // property and later called as `this.fetchImpl(...)`, because the
    // `this` receiver is no longer the Window. Chrome is lenient here
    // but Safari (and WebKit generally) enforces the binding strictly.
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(window);
  }

  // --- Retrieval ----------------------------------------------------------

  // `since`/`until` aren't accepted by `/api/list_roasts` — the handler
  // only filters by coffee_id + min_stars. When either date is set we
  // transparently route through `search_roasts`, which does accept
  // `date_from`/`date_to`. Callers don't need to know. `cursor` is the
  // opaque `next_cursor` from a previous page.
  async listRoasts(params?: ListRoastsParams): Promise<ListRoastsResponse> {
    if (params?.since || params?.until) {
      const envl = await this.getEnvelope<RoastPageData>("/api/search_roasts", {
        coffee_label: params.coffee_id,
        date_from: params.since,
        date_to: params.until,
        min_stars: params.min_stars,
        limit: params.limit,
        cursor: params.cursor,
      });
      return envelopeToRoastList(envl);
    }
    const envl = await this.getEnvelope<RoastPageData>("/api/list_roasts", {
      coffee_id: params?.coffee_id,
      min_stars: params?.min_stars,
      limit: params?.limit,
      cursor: params?.cursor,
    });
    return envelopeToRoastList(envl);
  }
  getRoast(roastId: string): Promise<RoastDetail> {
    return this.unwrap<RoastDetail>(
      `/api/get_roast/${encodeURIComponent(roastId)}`,
    );
  }
  // The backend returns row-oriented points from `ProfilePoint.model_dump`.
  // The chart consumes columnar arrays (see types.ts::Timeseries) because
  // Recharts wants one array per series. We transpose here so pages don't
  // have to know the wire format.
  async getTimeseries(
    roastId: string,
    opts?: { start_s?: number; end_s?: number; max_points?: number },
  ): Promise<Timeseries> {
    const envl = await this.getEnvelope<TimeseriesData>(
      `/api/get_roast_timeseries/${encodeURIComponent(roastId)}`,
      opts,
    );
    return transposeTimeseries(envl.data);
  }
  async searchRoasts(params: {
    coffee_id?: string;
    since?: string;
    until?: string;
    min_stars?: number;
    tag?: string;
    limit?: number;
    cursor?: string | null;
  }): Promise<ListRoastsResponse> {
    // REST param names differ from ours — remap before sending.
    const envl = await this.getEnvelope<RoastPageData>("/api/search_roasts", {
      coffee_label: params.coffee_id,
      date_from: params.since,
      date_to: params.until,
      min_stars: params.min_stars,
      tag: params.tag,
      limit: params.limit,
      cursor: params.cursor,
    });
    return envelopeToRoastList(envl);
  }
  async getCoffeeHistory(coffee_label: string): Promise<ListRoastsResponse> {
    // `data.summary` here is a structured recap of the coffee's history;
    // we drop it and keep just the row list + envelope text. The history
    // is the whole answer, not a page — the backend applies its own
    // ceiling — so total == count and nothing is truncated.
    const envl = await this.getEnvelope<{ rows: RoastPageData["rows"]; summary: unknown }>(
      "/api/get_coffee_history",
      { coffee_label },
    );
    return envelopeToRoastList({ text: envl.text, data: { rows: envl.data.rows ?? [] } });
  }

  // --- Milestones ---------------------------------------------------------

  // Model dump plus the backend-computed `effective_*` view. Prefer this
  // over `roast.milestones` whenever the effective value matters — the
  // precedence rules live on the server and are not duplicated here.
  getDetectedMilestones(roastId: string): Promise<DetectedMilestonesData> {
    return this.unwrap<DetectedMilestonesData>(
      `/api/get_detected_milestones/${encodeURIComponent(roastId)}`,
    );
  }
  findMarkerDiscrepancies(
    roastId: string,
    opts?: { tolerance_s?: number },
  ): Promise<Envelope<MarkerDiscrepanciesData>> {
    return this.getEnvelope(
      `/api/find_marker_discrepancies/${encodeURIComponent(roastId)}`,
      opts,
    );
  }
  reviewMilestoneConfidence(
    roastId: string,
    opts?: { min_confidence?: number },
  ): Promise<Envelope<MilestoneConfidenceData>> {
    return this.getEnvelope(
      `/api/review_milestone_confidence/${encodeURIComponent(roastId)}`,
      opts,
    );
  }
  correctMilestone(roastId: string, body: CorrectionInput): Promise<unknown> {
    return this.postUnwrap(
      `/api/correct_milestone/${encodeURIComponent(roastId)}`,
      body,
    );
  }
  clearMilestoneCorrection(
    roastId: string,
    body: { milestone_type: string; reason?: string | null },
  ): Promise<unknown> {
    return this.postUnwrap(
      `/api/clear_milestone_correction/${encodeURIComponent(roastId)}`,
      body,
    );
  }
  listCorrections(roastId: string): Promise<unknown> {
    return this.unwrap(
      `/api/list_corrections/${encodeURIComponent(roastId)}`,
    );
  }

  // --- Evaluation / rating -----------------------------------------------

  // `Evaluation.to_dict()` — read `evidence` before `headline`.
  evaluateRoast(roastId: string): Promise<Evaluation> {
    return this.unwrap<Evaluation>(
      `/api/evaluate_roast/${encodeURIComponent(roastId)}`,
    );
  }
  rateRoast(
    roastId: string,
    body: RatingInput,
  ): Promise<{ ok: boolean; roast_id: string; sidecar: string | null; rating: Rating }> {
    return this.postUnwrap(`/api/rate_roast/${encodeURIComponent(roastId)}`, body);
  }
  // Backend returns `{roast_id, is_rated, rating}`; components read
  // `r.rating`, so unwrapping `data` is all they need.
  getRating(
    roastId: string,
  ): Promise<{ roast_id: string; is_rated: boolean; rating: Rating | null }> {
    return this.unwrap(`/api/get_rating/${encodeURIComponent(roastId)}`);
  }
  async listUnratedRoasts(opts?: {
    limit?: number;
    cursor?: string | null;
  }): Promise<ListRoastsResponse> {
    const envl = await this.getEnvelope<RoastPageData>(
      "/api/list_unrated_roasts",
      opts,
    );
    return envelopeToRoastList(envl);
  }

  // --- AI evaluation -----------------------------------------------------

  // Returns the envelope as-is so the page can render both the
  // `text` (the LLM narrative) and `data.grounding` (what we sent
  // it — handy for debugging or a "show your work" disclosure).
  aiEvaluateRoast(
    roastId: string,
    model?: string,
  ): Promise<Envelope<AIEvaluation>> {
    const path = `/api/ai_evaluate_roast/${encodeURIComponent(roastId)}`
      + (model ? `?model=${encodeURIComponent(model)}` : "");
    return this.send<Envelope<AIEvaluation>>(this.baseUrl + path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
    });
  }

  // --- Recipe trend ------------------------------------------------------

  async getRecipeTrend(params: {
    coffee_id: string;
    roast_level?: string;
    batch_size_min_g?: number;
    batch_size_max_g?: number;
  }): Promise<RecipeTrend> {
    const envl = await this.getEnvelope<RecipeTrendData>(
      "/api/get_recipe_trend",
      params as Record<string, unknown>,
    );
    return { summary: envl.text, ...envl.data };
  }

  // --- Maintenance -------------------------------------------------------

  async getMaintenanceStatus(): Promise<MaintenanceStatusResponse> {
    const envl = await this.getEnvelope<MaintenanceStatusData>(
      "/api/get_maintenance_status",
    );
    return { summary: envl.text, ...envl.data };
  }

  async listMaintenanceHistory(limit = 50): Promise<MaintenanceEvent[]> {
    const envl = await this.getEnvelope<{ count: number; events: MaintenanceEvent[] }>(
      "/api/list_maintenance_history",
      { limit },
    );
    return envl.data.events ?? [];
  }

  logMaintenance(body: {
    maintenance_type: string;
    performed_at?: string | null;
    notes?: string;
  }): Promise<MaintenanceEvent> {
    return this.postUnwrap<MaintenanceEvent>("/api/log_maintenance", body);
  }

  // --- Coffee profiles ---------------------------------------------------

  addCoffeeProfile(
    profile: Partial<CoffeeProfile> & { coffee_id: string; name: string },
  ): Promise<{ ok: boolean; profile: CoffeeProfile }> {
    return this.postUnwrap("/api/add_coffee_profile", profile);
  }
  getCoffeeProfile(coffee_id: string): Promise<{ profile: CoffeeProfile | null }> {
    return this.unwrap(
      `/api/get_coffee_profile/${encodeURIComponent(coffee_id)}`,
    );
  }
  listCoffeeProfiles(): Promise<{ profiles: CoffeeProfile[] }> {
    return this.unwrap("/api/list_coffee_profiles");
  }

  // --- Analysis ----------------------------------------------------------

  // Two lists under `data` (issue #22): `improvements` is advice backed by
  // rated roasts; `drift` describes how this batch differs from the
  // typical one. The server defaults are `only_rated=false, min_stars=4`.
  //
  // These three methods deliberately return the full envelope because the
  // UI surfaces the `text` line next to the rich view.
  getImprovementSuggestions(
    roastId: string,
    opts?: { only_rated?: boolean; min_stars?: number; use_reference?: boolean },
  ): Promise<Envelope<ImprovementSuggestionsData>> {
    return this.getEnvelope(
      `/api/get_improvement_suggestions/${encodeURIComponent(roastId)}`,
      opts,
    );
  }

  // Hero roast for a coffee. `coffee_label` accepts either coffee_id
  // (preferred — stable across renames) or display name.
  getReferenceRoast(
    coffee_label: string,
  ): Promise<Envelope<ReferenceRoastResponseData>> {
    return this.getEnvelope("/api/get_reference_roast", { coffee_label });
  }

  // Per-coffee consistency scorecard (score 0–100 + per-metric stats).
  // `report` is null when the coffee has <2 roasts.
  getConsistencyReport(
    coffee_label: string,
  ): Promise<Envelope<ConsistencyReportResponseData>> {
    return this.getEnvelope("/api/get_consistency_report", { coffee_label });
  }

  // --- Ingestion ---------------------------------------------------------

  async getIngestionStatus(): Promise<IngestionStatus> {
    const envl = await this.getEnvelope<IngestionStatusData>(
      "/api/get_ingestion_status",
    );
    return adaptIngestionStatus(envl);
  }

  // --- Low-level ---------------------------------------------------------

  // Parses + returns the envelope as-is. Use when the caller wants both
  // `text` and `data`.
  private async getEnvelope<T>(
    path: string,
    params?: Record<string, unknown>,
  ): Promise<Envelope<T>> {
    const url = new URL(this.baseUrl + path);
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === "") continue;
        url.searchParams.set(k, String(v));
      }
    }
    return this.send<Envelope<T>>(url.toString(), { method: "GET" });
  }

  // Shortcut: GET an envelope and return `data` only.
  private async unwrap<T>(
    path: string,
    params?: Record<string, unknown>,
  ): Promise<T> {
    const envl = await this.getEnvelope<T>(path, params);
    return envl.data;
  }

  // Shortcut: POST an envelope and return `data` only.
  private async postUnwrap<T>(path: string, body: unknown): Promise<T> {
    const envl = await this.send<Envelope<T>>(this.baseUrl + path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
    return envl.data;
  }

  private async send<T>(url: string, init: RequestInit): Promise<T> {
    const token = await this.getIdToken();
    const headers = new Headers(init.headers ?? {});
    headers.set("authorization", `Bearer ${token}`);
    headers.set("accept", "application/json");
    const resp = await this.fetchImpl(url, { ...init, headers });
    const text = await resp.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }
    if (!resp.ok) {
      const message =
        (parsed && typeof parsed === "object" && "detail" in parsed
          ? String((parsed as { detail: unknown }).detail)
          : null) ?? `HTTP ${resp.status}`;
      throw new ApiError(resp.status, message, parsed);
    }
    return parsed as T;
  }
}
